// deps
import React, { memo, ReactElement, ReactNode, useCallback } from "react";
import { AriaHidden, BottomSheet } from "@swiggy-private/core-ui";
import cx from "classnames";

// internal deps
import { User } from "@swiggy-private/payment-core";
import { TextButton } from "@swiggy-private/dls-web-core/dist/components/TextButton";

// interfaces
import { LinkDialogActions } from "@payment-ui/interfaces/LinkAccount";

// constants
import { TEXTS } from "@payment-ui/constants/LinkAccount";

//components
import { LinkAccountContent } from "@payment-ui/components/link-account-content";

import styles from "./LinkAccount.v4.scss";

interface LinkProps {
    label?: string;
    renderHeader?: () => ReactNode;
    renderContent?: (user: User) => ReactNode;
    closeDialog?: VoidFunction;
    canShowDialog?: boolean;
    actions?: LinkDialogActions;
    labelClassName?: string;
    user?: User;
    "data-testid"?: string;
}

function Link(props: LinkProps): ReactElement | null {
    const { renderContent, renderHeader, actions, user, labelClassName, canShowDialog, closeDialog } = props;

    const labelClass = cx(styles.label, labelClassName);
    const onCancel = useCallback((): void => {
        if (typeof closeDialog === "function") {
            closeDialog();
        }
    }, [closeDialog]);
    const onConfirm = useCallback((): void => {
        if (typeof closeDialog === "function") {
            closeDialog();
        }
        if (typeof actions?.onProceed === "function") {
            actions.onProceed();
        }
    }, [actions, closeDialog]);

    const renderDialogContent = useCallback(() => {
        if (typeof renderContent === "function" && user) {
            return renderContent(user);
        }
    }, [renderContent, user]);

    return (
        <>
            {canShowDialog && (
                <>
                    <BottomSheet hideOnOverlayClick={true} onClose={onCancel}>
                        {({ close }): React.ReactElement => (
                            <LinkAccountContent
                                renderHeader={renderHeader}
                                renderContent={renderDialogContent}
                                primaryAction={{
                                    text: TEXTS.CONFIRM_AND_PROCEED,
                                    onClick: onConfirm,
                                }}
                                secondaryAction={{
                                    text: TEXTS.LINK_DIALOG_SECONDARYRY_ACTION_TEXT,
                                    onClick: close,
                                }}
                            />
                        )}
                    </BottomSheet>
                </>
            )}

            <AriaHidden as="span">
                <TextButton
                    textAlign="right"
                    className={labelClass}
                    buttonSize="small"
                    color="primary"
                    data-testid={props["data-testid"]}
                >
                    {props.label ?? TEXTS.DEFAULT_LABEL}
                </TextButton>
            </AriaHidden>
        </>
    );
}

export default memo(Link);
